import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import {
  Button,
  Icons,
  SearchInput,
  Typography,
  useLayoutGutter,
  View,
  XStack,
} from '@usepepper/sprout-kit-ui';
import {
  countActiveCustomerFilters,
  CustomersFilterDialog,
} from '@finance-hub/domain/customers/components/customers-filter-dialog';
import {
  CustomerFilterField,
  CustomerFilters,
  CustomerPresenceFilter,
  CustomerSortField,
  CustomerYesNoFilter,
  FinanceHubCustomerRow,
} from '@finance-hub/domain/customers/blocs/types/customer-list';
import { CustomersTable } from '@finance-hub/domain/customers/components/customers-table';
import { CustomersTableEmptyState } from '@finance-hub/domain/customers/components/customers-table-empty-state';
import { CustomersTableFullListError } from '@finance-hub/domain/customers/components/customers-table-full-list-error';
import { CustomersTableSkeletonRows } from '@finance-hub/domain/customers/components/customers-table-skeleton-rows';
import {
  DEFAULT_CUSTOMER_SORT,
  getNextCustomerSort,
  sortCustomerRows,
} from '@finance-hub/domain/customers/blocs/utils/customer-list-sort-utils';
import { ErrorState } from '@finance-hub/components/error-state';
import { TableScrollHandle } from '@experiences/components/table/types';
import { useCustomerFilterStore } from '@finance-hub/domain/customers/blocs/stores/use-customer-filter-store';
import { useCustomerListLoader } from '@finance-hub/domain/customers/blocs/loaders/use-customer-list-loader';
import { useDebouncedCallback } from 'use-debounce';

const FILTER_FIELD_TO_ROW_VALUE: Record<
  CustomerFilterField,
  (row: FinanceHubCustomerRow) => boolean
> = {
  autopay: (row) => row.hasAutopay,
  contactInfo: (row) => row.hasContactInfo,
  paymentMethod: (row) => row.hasPaymentMethod,
  paymentPlan: (row) => row.hasPaymentPlan,
  statement: (row) => row.hasStatement,
};

const SELECTION_TO_REQUIRED_VALUE: Record<
  CustomerPresenceFilter | CustomerYesNoFilter,
  boolean | null
> = {
  all: null,
  missing: false,
  no: false,
  onFile: true,
  yes: true,
};

const FILTER_FIELDS = Object.keys(
  FILTER_FIELD_TO_ROW_VALUE,
) as CustomerFilterField[];

const filterCustomerRows = (
  rows: FinanceHubCustomerRow[],
  filters: CustomerFilters,
) =>
  rows.filter((row) =>
    FILTER_FIELDS.every((field) => {
      const requiredValue = SELECTION_TO_REQUIRED_VALUE[filters[field]];

      return (
        requiredValue === null ||
        FILTER_FIELD_TO_ROW_VALUE[field](row) === requiredValue
      );
    }),
  );

const searchCustomerRows = (rows: FinanceHubCustomerRow[], search: string) => {
  const query = search.trim().toLowerCase();

  if (!query) {
    return rows;
  }

  return rows.filter((row) =>
    [row.accountIdLabel, row.addressLabel, row.customerNameLabel].some(
      (value) => value?.toLowerCase().includes(query),
    ),
  );
};

const LOADING_SKELETON_ROW_COUNT = 8;
const FULL_LIST_LOADING_SKELETON_ROW_COUNT = 3;
const SEARCH_DEBOUNCE_MS = 150;

export const CustomersTableContainer: React.FC = () => {
  const filters = useCustomerFilterStore((state) => state.filters);
  const applyFilters = useCustomerFilterStore((state) => state.applyFilters);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState(DEFAULT_CUSTOMER_SORT);
  const tableScrollHandleRef = useRef<TableScrollHandle | null>(null);
  const gutter = useLayoutGutter();
  const { error, fullListError, fullListLoading, loading, refetch, rows } =
    useCustomerListLoader();

  const applySearch = useDebouncedCallback((text: string) => {
    setSearch(text);
    tableScrollHandleRef.current?.scrollToTop();
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    applySearch(text);
  };

  const flushSearch = () => applySearch.flush();

  const handleFiltersPress = () => {
    if (isCompleteListPending) {
      return;
    }

    setIsFilterDialogOpen(true);
  };

  const handleColumnPress = (field: CustomerSortField) => {
    setSort((current) => getNextCustomerSort(current, field));
    tableScrollHandleRef.current?.scrollToTop();
  };

  const activeFilterCount = countActiveCustomerFilters(filters);
  // Distinguishes "no customers yet" from "the filters hid them all"
  const hasAnyCustomers = rows.length > 0;
  const hasActiveFilters = activeFilterCount > 0;
  // Disabled until the complete list arrives so nobody searches, filters or sorts the initial 50-row snapshot
  const isCompleteListPending = loading || fullListLoading || !!fullListError;
  const visibleRows = sortCustomerRows(
    filterCustomerRows(searchCustomerRows(rows, search), filters),
    sort,
  );

  if (error && rows.length === 0) {
    return (
      <ErrorState
        onRetry={refetch}
        title={i18n.t('finance_hub.customers.table.error')}
      />
    );
  }

  return (
    <View flex={1} gap="$8">
      <XStack alignItems="center" gap="$8" paddingHorizontal={gutter}>
        <SearchInput
          clearAriaLabel={i18n.t('clear_search')}
          disabled={isCompleteListPending}
          flexGrow={1}
          maxWidth="$284"
          onChangeText={handleSearchChange}
          onClear={flushSearch}
          onSubmit={flushSearch}
          placeholder={i18n.t('finance_hub.customers.search_placeholder')}
          value={searchText}
        />
        <Button
          alignSelf="center"
          disabled={isCompleteListPending}
          icon={
            <Icons.ListFilter
              color={hasActiveFilters ? '$iconOnDark' : '$iconPrimary'}
              size="$16"
            />
          }
          onPress={handleFiltersPress}
          size="$sm"
          variant={hasActiveFilters ? 'primary' : 'secondary'}
        >
          {hasActiveFilters
            ? i18n.t('filters_with_count', { count: activeFilterCount })
            : i18n.t('filters')}
        </Button>
        {/* Counting the initial 50-row snapshot would misstate the total, so
            the count waits for the complete list like the controls do */}
        {isCompleteListPending ? null : (
          <Typography color="$textSecondary" marginLeft="auto" size="$xs">
            {i18n.t('finance_hub.customers.visible_count', {
              total: rows.length,
              visible: visibleRows.length,
            })}
          </Typography>
        )}
      </XStack>
      <CustomersFilterDialog
        filters={filters}
        isOpen={isFilterDialogOpen}
        onApply={applyFilters}
        onClose={() => setIsFilterDialogOpen(false)}
      />
      <CustomersTable
        emptyComponent={
          loading ? (
            <View flex={1} width="100%">
              <CustomersTableSkeletonRows count={LOADING_SKELETON_ROW_COUNT} />
            </View>
          ) : (
            <CustomersTableEmptyState
              description={i18n.t(
                hasAnyCustomers
                  ? 'finance_hub.customers.table.no_results.description'
                  : 'finance_hub.customers.table.empty.description',
              )}
              title={i18n.t(
                hasAnyCustomers
                  ? 'finance_hub.customers.table.no_results.title'
                  : 'finance_hub.customers.table.empty.title',
              )}
            />
          )
        }
        listFooter={
          fullListLoading ? (
            <CustomersTableSkeletonRows
              count={FULL_LIST_LOADING_SKELETON_ROW_COUNT}
            />
          ) : fullListError ? (
            <CustomersTableFullListError onRetry={refetch} />
          ) : null
        }
        onColumnPress={isCompleteListPending ? undefined : handleColumnPress}
        rows={visibleRows}
        scrollHandleRef={tableScrollHandleRef}
        sort={sort}
      />
    </View>
  );
};
